import { useCallback, useRef, useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { useDatasetDataRes, useDatasetError } from '../types/types';
import { replaceCurrencyInData } from '../../utils/managerUtils';
import {
  DEFAULT_CURRENCY_FORMAT,
  DEFAULT_CURRENCY_PLACEHOLDER,
  ENTRY_ERROR,
  FORCE_ERROR,
  SET_DEFAULT_CURRENCY_FORMAT,
  X_CACHEDQUERY_ID,
  X_CACHEDQUERY_TOTALCNT,
} from '../../constants';
import { detailedApiMethodFetcher } from '../utils/fetchers/detailedApiMethodFetcher';
import { ResolvedApiMethod } from '../types/types';
import { requestDatasetData } from '../requests/dataset/requestDatasetData';
import { requestDefaultCurrency } from '../requests/requestDefaultCurrency';
import { debugLogger } from '../../debug/DebugLogger';

type UseDatasetDataOptions = {
  // Consumers with no inline <ApiError> surface must handle errors themselves.
  rethrowErrors?: boolean;
};

export const useDatasetData = ({
  rethrowErrors = false,
}: UseDatasetDataOptions = {}) => {
  const {
    dispatch,
    state: { token, defaultCurrencyFormat },
  } = useUserState();
  const [loadingDatasetId, setLoadingDatasetId] = useState<string | undefined>(
    undefined
  );
  const [error, setError] = useState<useDatasetError>(undefined);
  const [data, setData] = useState<useDatasetDataRes>(undefined);
  // Bumped on cancel() to logically drop in-flight requests: CapacitorHttp has
  // no real abort, so a request that resolves after the user left its dataset
  // is discarded by comparing its captured epoch against the current one.
  const requestEpochRef = useRef(0);
  const handleApiError = useApiErrorHandling();

  const initFetch = (datasetId: string) => {
    setLoadingDatasetId(datasetId);
    setData(undefined);
    setError(undefined);
  };

  const replaceCurrencyFormat = async (
    res: any,
    dataset: any,
    forceHeader?: string
  ) => {
    // Replace %m in formatItems with currency formatting string if default currency string was already loaded
    if (defaultCurrencyFormat) {
      res = replaceCurrencyInData(dataset, res, defaultCurrencyFormat);
    } else {
      // Check if data use %m in some formatItem, in which case the app needs to fetch default currency
      const usesDefaultCurrency = dataset.items.some((column) => {
        if (column.formatItem) {
          return res.some((row) =>
            row[column.formatItem]?.includes(DEFAULT_CURRENCY_PLACEHOLDER)
          );
        }
        return false;
      });

      if (usesDefaultCurrency) {
        let currencyFormat = DEFAULT_CURRENCY_FORMAT;

        try {
          const currencyRes = await requestDefaultCurrency(token, forceHeader);
          if (currencyRes?.format) {
            currencyFormat = currencyRes.format;
            // Save default currency format
            dispatch({
              type: SET_DEFAULT_CURRENCY_FORMAT,
              payload: currencyFormat,
            });
          }
        } catch (error) {
          console.error('Failed to fetch default currency', error);
        } finally {
          res = replaceCurrencyInData(dataset, res, currencyFormat);
        }
      }
    }
  };

  const getResult = (
    res: any,
    apiRes: any,
    dataset: any,
    lazy: boolean,
    api: string,
    requestId?: number,
    // Used when the server omits the totalCount header (customLimit locate
    // responses) but the caller knows the count from its id-probe.
    fallbackTotalCount?: number
  ) => {
    const newCachedQueryId = apiRes.headers[X_CACHEDQUERY_ID] || undefined;

    const result: useDatasetDataRes = {
      data: res,
      datasetId: dataset.datasetId,
      lazy,
      cachedQueryId: newCachedQueryId,
      totalCount: apiRes.headers[X_CACHEDQUERY_TOTALCNT]
        ? parseInt(apiRes.headers[X_CACHEDQUERY_TOTALCNT])
        : fallbackTotalCount,
      api,
      requestId,
    };

    /** @debugFeature lazyLoading Logs every finished dataset data load: api path, lazy flag, cached query id, total count and returned row count — never the rows themselves. */
    debugLogger.log('lazyLoading', 'dataset data loaded', () => ({
      datasetId: result.datasetId,
      api: result.api,
      lazy: result.lazy,
      requestId: result.requestId,
      cachedQueryId: result.cachedQueryId,
      totalCount: result.totalCount,
      rowCount: Array.isArray(res) ? res.length : undefined,
    }));

    setData(result);

    return result;
  };

  const detailedFetch = async (
    apiMethod: ResolvedApiMethod,
    dataset: any,
    lazy = false,
    forceHeader?: string,
    // Forwarded to openForceErrorModal as cancelAction (e.g. skip row on cancel)
    forceErrorCancelAction?: () => void,
    requestId?: number,
    fallbackTotalCount?: number
  ): Promise<useDatasetDataRes> => {
    let res;
    const epoch = requestEpochRef.current;
    try {
      initFetch(dataset.datasetId);
      const apiRes = await detailedApiMethodFetcher<any>(
        apiMethod,
        forceHeader,
        undefined,
        true
      );
      // Cancelled while in-flight (user left the dataset) — drop the result so
      // no state update, currency post-processing or side effect runs.
      if (epoch !== requestEpochRef.current) {
        return undefined;
      }
      res = Array.isArray(apiRes?.data) ? apiRes?.data : [apiRes?.data];
      await replaceCurrencyFormat(res, dataset, forceHeader);
      return getResult(
        res,
        apiRes,
        dataset,
        lazy,
        apiMethod.path,
        requestId,
        fallbackTotalCount
      );
    } catch (e) {
      // Swallow errors from a cancelled request — no toast, no force modal.
      if (epoch !== requestEpochRef.current) {
        return undefined;
      }
      // Force and entry errors must stay here — their modal and retry live in this catch.
      const isForceFlow =
        e instanceof Error &&
        (e.name === FORCE_ERROR || e.name === ENTRY_ERROR);
      if (rethrowErrors && !isForceFlow) {
        throw e;
      }
      if (e instanceof Error)
        setError({ error: e, datasetId: dataset.datasetId });
      const errorHandlingRes = await handleApiError(
        e,
        true,
        (forceHeader) =>
          detailedFetch(
            apiMethod,
            dataset,
            lazy,
            forceHeader,
            forceErrorCancelAction,
            requestId,
            fallbackTotalCount
          ),
        forceErrorCancelAction
      );

      // Force retry returns fresh dataset rows; propagate them to export callers.
      return errorHandlingRes instanceof Error || !errorHandlingRes
        ? undefined
        : errorHandlingRes;
    } finally {
      // cancel() already cleared loading state; don't clobber a newer request.
      if (epoch === requestEpochRef.current) {
        setLoadingDatasetId((prev) =>
          prev === dataset.datasetId ? undefined : prev
        );
      }
    }
  };

  const fetch = async (
    api: string,
    dataset: any,
    lazy = false,
    cachedQueryId: undefined | string = undefined,
    forceHeader?: string,
    // Forwarded to openForceErrorModal as cancelAction (e.g. skip row on cancel)
    forceErrorCancelAction?: () => void,
    requestId?: number,
    fallbackTotalCount?: number
  ): Promise<useDatasetDataRes> => {
    let res;
    const epoch = requestEpochRef.current;
    try {
      initFetch(dataset.datasetId);
      const apiRes = await requestDatasetData(
        api,
        token,
        cachedQueryId,
        forceHeader
      );
      // Cancelled while in-flight (user left the dataset) — drop the result so
      // no state update, currency post-processing or side effect runs.
      if (epoch !== requestEpochRef.current) {
        return undefined;
      }
      res = Array.isArray(apiRes.data) ? apiRes.data : [apiRes.data];

      await replaceCurrencyFormat(res, dataset, forceHeader);
      return getResult(
        res,
        apiRes,
        dataset,
        lazy,
        api,
        requestId,
        fallbackTotalCount
      );
    } catch (e) {
      // Swallow errors from a cancelled request — no toast, no force modal.
      if (epoch !== requestEpochRef.current) {
        return undefined;
      }
      // Force and entry errors must stay here — their modal and retry live in this catch.
      const isForceFlow =
        e instanceof Error &&
        (e.name === FORCE_ERROR || e.name === ENTRY_ERROR);
      if (rethrowErrors && !isForceFlow) {
        throw e;
      }
      if (e instanceof Error)
        setError({ error: e, datasetId: dataset.datasetId });
      const errorHandlingRes = await handleApiError(
        e,
        true,
        (forceHeader) =>
          fetch(
            api,
            dataset,
            lazy,
            cachedQueryId,
            forceHeader,
            forceErrorCancelAction,
            requestId,
            fallbackTotalCount
          ),
        forceErrorCancelAction
      );

      // Force retry returns fresh dataset rows; propagate them to export callers.
      return errorHandlingRes instanceof Error || !errorHandlingRes
        ? undefined
        : errorHandlingRes;
    } finally {
      // cancel() already cleared loading state; don't clobber a newer request.
      if (epoch === requestEpochRef.current) {
        setLoadingDatasetId((prev) =>
          prev === dataset.datasetId ? undefined : prev
        );
      }
    }
  };

  // Logically cancels any in-flight fetch/detailedFetch: bumps the epoch so a
  // late response is dropped, and clears the loading flag so a stale spinner
  // doesn't stick when no follow-up fetch starts (e.g. leaving the manager).
  const cancel = () => {
    requestEpochRef.current += 1;
    setLoadingDatasetId(undefined);
    // Drop the error of the dataset we're leaving — otherwise the apiError memo
    // re-surfaces it as a full-page ApiError the moment we return to that
    // datasetId (stale error flash).
    setError(undefined);
  };

  const reset = () => {
    setData(undefined);
  };

  // Drop only the error, without touching the epoch or loading flag. Used to
  // discard a long-running poll error that arrived while the user is in a form
  // opened before data first loaded — the form must not be covered, and the
  // list re-fetches on return instead of surfacing that stale error. Stable so
  // consumers can list it in effect deps without re-running every render.
  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return {
    isLoading: loadingDatasetId !== undefined,
    loadingDatasetId,
    error,
    data,
    fetch,
    detailedFetch,
    cancel,
    reset,
    clearError,
  };
};
